/**
 * DeliverableMatchIndicator
 *
 * Renders as a compact sub-row underneath a task row. Each deliverable
 * with matches is shown as a "suggested document" chip. Hovering reveals
 * match details; clicking the document name opens the document viewer.
 */
import { Eye, FileSearch, Sparkles } from 'lucide-react';

import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from '@/components/ui/hover-card';
import { cn } from '@/lib/utils';
import type {
  DeliverableMatchSchemaRead,
  DeliverableSchemaRead,
} from '@/services/api/deliverablesServicev2';

const getConfidenceColor = (confidence: number) => {
  if (confidence >= 0.8)
    return 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400';
  if (confidence >= 0.6)
    return 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400';
  return 'bg-zinc-100 text-zinc-600 dark:bg-zinc-800 dark:text-zinc-400';
};

const ConfidenceBadge = ({ confidence }: { confidence: number }) => (
  <span
    className={cn(
      'text-[10px] font-medium px-1.5 py-0.5 rounded inline-block',
      getConfidenceColor(confidence),
    )}
  >
    {Math.round(confidence * 100)}% confidence
  </span>
);

/** Get the display name from an artifact on a match */
const getMatchDocumentName = (match: DeliverableMatchSchemaRead) =>
  match.artifact?.blob?.name ||
  match.artifact?.artifact_digest?.title ||
  match.artifact?.name ||
  'Document';

/** Get all matches worth showing for a deliverable (approved or needs review) */
const getRelevantMatches = (deliverable: DeliverableSchemaRead) =>
  deliverable.matches?.filter(
    (m) => m.status === 'Approved' || m.status === 'Needs Review',
  ) ?? [];

/** A single deliverable chip with hover detail and document quick-link */
const DeliverableChip = ({
  deliverable,
  onViewDocument,
}: {
  deliverable: DeliverableSchemaRead;
  onViewDocument?: (artifactId: string) => void;
}) => {
  const matches = getRelevantMatches(deliverable);
  const matchCount = matches.length;

  return (
    <HoverCard openDelay={300} closeDelay={100}>
      <HoverCardTrigger asChild>
        <button
          type="button"
          className="inline-flex items-center gap-1 rounded-full bg-blue-50 px-2 py-0.5 text-[10px] font-medium text-blue-700 transition-colors hover:bg-blue-100 dark:bg-blue-900/20 dark:text-blue-400 dark:hover:bg-blue-900/30"
        >
          <FileSearch className="h-2.5 w-2.5" />
          <span className="truncate max-w-[160px]">{deliverable.name}</span>
          {matchCount > 1 && (
            <span className="opacity-70">{matchCount}</span>
          )}
        </button>
      </HoverCardTrigger>
      <HoverCardContent align="start" side="bottom" className="w-72 p-0">
        <div className="divide-y divide-zinc-100 dark:divide-zinc-800">
          {/* Header */}
          <div className="px-3 py-2 bg-zinc-50 dark:bg-zinc-900/50">
            <p className="text-xs font-semibold text-zinc-900 dark:text-white">
              {deliverable.name}
            </p>
            <p className="text-[10px] text-zinc-500 dark:text-zinc-400 mt-0.5">
              {matchCount === 1
                ? 'Suggested document match'
                : `${matchCount} suggested matches`}
            </p>
          </div>

          {/* Match rows — each clickable to open the document */}
          {matches.slice(0, 3).map((match) => {
            const artifactId =
              typeof match.artifact_id === 'string'
                ? match.artifact_id
                : null;
            const docName = getMatchDocumentName(match);

            return (
              <div key={match.id} className="px-3 py-2 space-y-1">
                <div className="flex items-center justify-between gap-1.5">
                  <span className="text-xs font-medium text-zinc-700 dark:text-zinc-300 truncate">
                    {docName}
                  </span>
                  {artifactId && onViewDocument && (
                    <button
                      type="button"
                      onClick={() => onViewDocument(artifactId)}
                      className="inline-flex items-center gap-0.5 rounded px-1.5 py-0.5 text-[10px] font-medium text-blue-600 hover:bg-blue-50 dark:text-blue-400 dark:hover:bg-blue-900/20 transition-colors flex-shrink-0"
                    >
                      <Eye className="h-2.5 w-2.5" />
                      View
                    </button>
                  )}
                </div>
                {match.confidence != null && (
                  <ConfidenceBadge confidence={match.confidence} />
                )}
                {match.context?.rationale && (
                  <p className="text-[10px] text-zinc-500 dark:text-zinc-400 leading-relaxed line-clamp-2">
                    {match.context.rationale}
                  </p>
                )}
              </div>
            );
          })}
        </div>
      </HoverCardContent>
    </HoverCard>
  );
};

interface DeliverableMatchIndicatorProps {
  taskDeliverables: DeliverableSchemaRead[];
  onViewDocument?: (artifactId: string) => void;
}

export const DeliverableMatchIndicator = ({
  taskDeliverables,
  onViewDocument,
}: DeliverableMatchIndicatorProps) => {
  // Show any deliverable that has at least one match (approved or needs review)
  const deliverablesWithMatches = taskDeliverables.filter(
    (d) => getRelevantMatches(d).length > 0,
  );

  if (deliverablesWithMatches.length === 0) {
    return null;
  }

  return (
    <div className="flex items-center gap-1.5 pb-1.5 pl-[88px]">
      <Sparkles className="h-3 w-3 text-blue-500 dark:text-blue-400 flex-shrink-0" />
      <div className="flex items-center gap-1 flex-wrap">
        {deliverablesWithMatches.map((d) => (
          <DeliverableChip
            key={d.id}
            deliverable={d}
            onViewDocument={onViewDocument}
          />
        ))}
      </div>
    </div>
  );
};
